<template>
  <div class="payment-details">
    <h3>Payment Details</h3>
    <div class="detail-row">
      <span class="label">Transaction ID:</span>
      <div class="transaction-id-block">
        <span class="transaction-id">{{ paymentData.id }}</span>
        <button @click="copyTransactionId" class="copy-btn" title="Copy Transaction ID">
          <svg viewBox="0 0 24 24" class="copy-icon">
            <path d="M16 1H4C2.9 1 2 1.9 2 3V17H4V3H16V1ZM19 5H8C6.9 5 6 5.9 6 7V21C6 22.1 6.9 23 8 23H19C20.1 23 21 22.1 21 21V7C21 5.9 20.1 5 19 5ZM19 21H8V7H19V21Z"/>
          </svg>
        </button>
      </div>
    </div>
    <div class="detail-row">
      <span class="label">Total Amount:</span>
      <span class="value">${{ (paymentData.total).toFixed(2) }}</span>
    </div>
    <div class="detail-row">
      <span class="label">Payment Method:</span>
      <span class="value">{{ paymentData.paymentMethod }}</span>
    </div>
  </div>
</template>

<script setup>
const props = defineProps({
  paymentData: {
    type: Object,
    required: true
  }
})

const emit = defineEmits(['copy-success', 'copy-error'])

const copyTransactionId = async () => {
  try {
    await navigator.clipboard.writeText(props.paymentData.id)
    emit('copy-success', 'Transaction ID copied to clipboard!')
  } catch (error) {
    emit('copy-error', 'Failed to copy transaction ID')
  }
}
</script>

<style scoped>
.payment-details {
  background: var(--color-background-mute);
  border: 1px solid var(--color-border);
  border-radius: 8px;
  padding: 1.5rem;
  margin: 2rem 0;
}

@media (max-width: 450px) {
  .payment-details {
    padding: 1rem;
    margin: 1rem 0;
  }
}

.payment-details h3 {
  margin-top: 0;
  margin-bottom: 1rem;
  color: var(--color-heading);
}

@media (max-width: 450px) {
  .payment-details h3 {
    font-size: 1.25rem;
  }
}

.detail-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem 0;
  border-bottom: 1px solid var(--color-border);
  gap: 1rem;
}

@media (max-width: 450px) {
  .detail-row {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
    padding: 0.75rem 0;
  }
}

.detail-row:last-child {
  border-bottom: none;
  margin-bottom: 0;
}

.label {
  font-weight: 600;
  color: var(--color-text);
  white-space: nowrap;
  flex-shrink: 0;
}

.value {
  color: var(--color-heading);
  font-family: monospace;
  word-break: break-all;
  text-align: right;
}

@media (max-width: 450px) {
  .value {
    text-align: left;
  }
}

.transaction-id-block {
  display: flex;
  align-items: center;
  background: var(--color-background);
  border: 1px solid var(--color-border);
  border-radius: 6px;
  padding: 0.5rem;
  gap: 0.5rem;
  min-width: 0;
}

@media (max-width: 450px) {
  .transaction-id-block {
    width: 100%;
  }
}

.transaction-id {
  font-family: monospace;
  color: var(--color-heading);
  white-space: nowrap;
  overflow-x: auto;
  flex: 1;
  font-size: 0.9rem;
  scrollbar-width: thin;
  scrollbar-color: rgba(0, 0, 0, 0.2) transparent;
}

.copy-btn {
  background: none;
  border: 1px solid var(--color-border);
  border-radius: 4px;
  padding: 0.5rem 0.5rem 0.3rem;
  cursor: pointer;
  font-size: 0.8rem;
  transition: background-color 0.2s;
  flex-shrink: 0;
}

.copy-btn:hover {
  background: var(--color-background-mute);
}

.transaction-id::-webkit-scrollbar {
  height: 3px;
}

.transaction-id::-webkit-scrollbar-track {
  background: transparent;
}

.transaction-id::-webkit-scrollbar-thumb {
  background: rgba(0, 0, 0, 0.2);
  border-radius: 2px;
}

.transaction-id::-webkit-scrollbar-thumb:hover {
  background: rgba(0, 0, 0, 0.4);
}

.copy-icon {
  width: 14px;
  height: 14px;
  fill: currentColor;
}
</style>